import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import {
  BlockStack,
  Box,
  InlineStack,
  Text,
  Spinner,
  Banner,
  Select,
  Popover,
  ActionList,
  Button,
  TextField,
} from "@shopify/polaris";
import { CalendarIcon } from "@shopify/polaris-icons";
import { apiCall } from "../../helper/Common";
import { countryLabel } from "../../settings/countryLanguageOptions";

function ymdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Inclusive range for last N calendar days including today (N = 7 → today and 6 days back). */
function getPresetRange(preset) {
  if (preset === "all") return { from: null, to: null };
  const end = new Date();
  end.setHours(0, 0, 0, 0);
  const start = new Date(end);
  if (preset === "7d") start.setDate(start.getDate() - 6);
  else if (preset === "30d") start.setDate(start.getDate() - 29);
  else if (preset === "90d") start.setDate(start.getDate() - 89);
  else return { from: null, to: null };
  return { from: ymdLocal(start), to: ymdLocal(end) };
}

function initialRangeState() {
  const { from, to } = getPresetRange("7d");
  return { mode: "7d", from, to };
}

const MetricTile = memo(function MetricTile({ label, value, loading }) {
  return (
    <div className="age-analytics-metric-tile">
      <Text as="p" variant="bodyMd" fontWeight="semibold" alignment="center">
        {label}
      </Text>
      <div className="age-analytics-metric-tile__valueWrap">
        {loading ? (
          <span className="age-analytics-metric-tile__valueLoading">
            <Spinner accessibilityLabel={`Loading ${label}`} size="small" />
          </span>
        ) : (
          <p className="age-analytics-metric-tile__value">{value}</p>
        )}
      </div>
    </div>
  );
});

function AnalyticsSection() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ total: 0, verified: 0, unverified: 0 });
  const [country, setCountry] = useState("all");
  const [countryOptions, setCountryOptions] = useState([{ label: "All countries", value: "all" }]);
  const [rangeState, setRangeState] = useState(initialRangeState);
  const [popoverActive, setPopoverActive] = useState(false);
  const [popoverView, setPopoverView] = useState("menu");
  const [customDraftFrom, setCustomDraftFrom] = useState("");
  const [customDraftTo, setCustomDraftTo] = useState("");

  const rangeButtonLabel = useMemo(() => {
    const { mode, from, to } = rangeState;
    if (mode === "all") return "All time";
    if (mode === "7d") return "Last 7 days";
    if (mode === "30d") return "Last 30 days";
    if (mode === "90d") return "Last 90 days";
    if (mode === "custom" && from && to) return `${from} – ${to}`;
    return "Custom range";
  }, [rangeState]);

  const loadCountries = useCallback(async () => {
    try {
      const res = await apiCall("analytics/verification/countries", "GET");
      const list = res?.data?.countries || [];
      const codes = list
        .map((c) => (typeof c === "string" ? c : c && c.code != null ? String(c.code) : String(c)))
        .map((c) => c.trim().toUpperCase())
        .filter(Boolean);
      const unique = [...new Set(codes)].sort((a, b) => {
        if (a === "ALL") return -1;
        if (b === "ALL") return 1;
        return countryLabel(a).localeCompare(countryLabel(b), undefined, { sensitivity: "base" });
      });
      const opts = [
        { label: "All countries (aggregate)", value: "all" },
        ...unique.map((code) => ({
          label: countryLabel(code),
          value: code,
        })),
      ];
      setCountryOptions(opts);
    } catch (e) {
      console.warn("[AnalyticsSection] countries:", e);
    }
  }, []);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (country && country !== "all") qs.set("country", country);
      if (rangeState.from && rangeState.to) {
        qs.set("from", rangeState.from);
        qs.set("to", rangeState.to);
      }
      const path = qs.toString() ? `analytics/verification?${qs.toString()}` : "analytics/verification";
      const res = await apiCall(path, "GET");
      const d = res?.data;
      if (d) {
        setStats({
          total: Number(d.total) || 0,
          verified: Number(d.verified) || 0,
          unverified: Number(d.unverified) || 0,
        });
      }
    } catch (e) {
      const msg = typeof e === "string" ? e : e?.message || "Could not load analytics";
      setError(msg);
      setStats({ total: 0, verified: 0, unverified: 0 });
    } finally {
      setLoading(false);
    }
  }, [country, rangeState]);

  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const applyPreset = useCallback((preset) => {
    if (preset === "custom") {
      const { from, to } = getPresetRange("7d");
      setCustomDraftFrom(from || "");
      setCustomDraftTo(to || "");
      setPopoverView("custom");
      return;
    }
    const { from, to } = getPresetRange(preset);
    setRangeState({ mode: preset, from, to });
    setPopoverActive(false);
    setPopoverView("menu");
  }, []);

  const applyCustomRange = useCallback(() => {
    let a = String(customDraftFrom || "").trim();
    let b = String(customDraftTo || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(a) || !/^\d{4}-\d{2}-\d{2}$/.test(b)) return;
    if (a > b) {
      const t = a;
      a = b;
      b = t;
    }
    setRangeState({ mode: "custom", from: a, to: b });
    setPopoverActive(false);
    setPopoverView("menu");
  }, [customDraftFrom, customDraftTo]);

  const activator = (
    <Button
      icon={CalendarIcon}
      onClick={() => {
        setPopoverView("menu");
        setPopoverActive((o) => !o);
      }}
      disclosure="down"
    >
      {rangeButtonLabel}
    </Button>
  );

  const selectOptions = useMemo(() => countryOptions, [countryOptions]);

  return (
    <div className="age-analytics-section">
      <Text as="h2" variant="headingLg">
        Verification analytics
      </Text>
      <Text as="p" variant="bodySm" tone="subdued">
        Counts from verification events in your theme and checkout. Filter by date and country.
      </Text>

      <div className="age-analytics-overview-card">
        <div className="age-analytics-overview-card__header">
          <Text as="h3" variant="headingMd">
            Overview
          </Text>
          <Popover
            active={popoverActive}
            activator={activator}
            autofocusTarget="first-node"
            onClose={() => {
              setPopoverActive(false);
              setPopoverView("menu");
            }}
          >
            {popoverView === "menu" ? (
              <ActionList
                actionRole="menuitem"
                items={[
                  {
                    content: "Last 7 days",
                    onAction: () => applyPreset("7d"),
                  },
                  {
                    content: "Last 30 days",
                    onAction: () => applyPreset("30d"),
                  },
                  {
                    content: "Last 90 days",
                    onAction: () => applyPreset("90d"),
                  },
                  {
                    content: "All time",
                    onAction: () => applyPreset("all"),
                  },
                  {
                    content: "Custom range…",
                    onAction: () => applyPreset("custom"),
                  },
                ]}
              />
            ) : (
              <Box padding="400" minWidth="280px">
                <BlockStack gap="300">
                  <TextField
                    label="From"
                    type="date"
                    value={customDraftFrom}
                    onChange={setCustomDraftFrom}
                    autoComplete="off"
                  />
                  <TextField
                    label="To"
                    type="date"
                    value={customDraftTo}
                    onChange={setCustomDraftTo}
                    autoComplete="off"
                  />
                  <InlineStack gap="200" wrap={false}>
                    <Button variant="primary" onClick={applyCustomRange}>
                      Apply
                    </Button>
                    <Button
                      variant="plain"
                      onClick={() => {
                        setPopoverView("menu");
                      }}
                    >
                      Back
                    </Button>
                  </InlineStack>
                </BlockStack>
              </Box>
            )}
          </Popover>
        </div>

        <Box paddingBlockStart="300" maxWidth="320px">
          <Select label="Filter by country" options={selectOptions} value={country} onChange={setCountry} />
        </Box>

        {error ? (
          <Box paddingBlockStart="300">
            <Banner tone="critical" title="Could not load analytics" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          </Box>
        ) : null}

        <div className="age-analytics-metrics">
          <MetricTile label="Total verification" value={stats.total} loading={loading} />
          <MetricTile label="Verified" value={stats.verified} loading={loading} />
          <MetricTile label="Unverified" value={stats.unverified} loading={loading} />
        </div>
      </div>
    </div>
  );
}

export default memo(AnalyticsSection);
